/**
 * Accessibility helpers:
 * 1. Focus trap + restore for drawers/modals
 * 2. aria-expanded toggle for dropdown/accordion triggers
 * 3. New-window announcer for target="_blank" links
 */

const FOCUSABLE_SELECTORS = [
  'summary',
  'a[href]',
  'button:enabled',
  '[tabindex]:not([tabindex^="-"])',
  '[draggable]',
  'area',
  'input:not([type=hidden]):enabled',
  'select:enabled',
  'textarea:enabled',
  'object',
  'iframe',
].join(', ')

const NEW_WINDOW_ANNOUNCEMENT_CLASS = 'js-a11y-new-window'
const ARIA_EXPANDED_TRIGGER_SELECTOR = '[data-a11y-expanded]'

const trapFocusHandlers = {}
let previouslyFocusedElement = null

function getFocusableElements(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS))
}

function isElementVisible(element) {
  if (!element || element.hidden) return false

  const style = window.getComputedStyle(element)
  if (style.display === 'none' || style.visibility === 'hidden') return false

  return element.getClientRects().length > 0
}

function isTabbableElement(element) {
  if (!(element instanceof HTMLElement)) return false
  if (element.closest('[hidden], .hidden, .visibility-hidden, [inert]')) {
    return false
  }
  if (element.matches(':disabled')) return false
  if (element.getAttribute('tabindex') === '-1') return false

  return isElementVisible(element)
}

function isProgrammaticallyFocusable(element) {
  if (!(element instanceof HTMLElement)) return false
  if (element.closest('[hidden], .hidden, .visibility-hidden, [inert]')) {
    return false
  }
  if (element.matches(':disabled')) return false
  if (!isElementVisible(element)) return false

  return isTabbableElement(element) || element.hasAttribute('tabindex')
}

function getTabbableElements(container) {
  return getFocusableElements(container).filter(isTabbableElement)
}

function removeTrapFocusListeners() {
  document.removeEventListener('focusin', trapFocusHandlers.focusin)
  document.removeEventListener('focusout', trapFocusHandlers.focusout)
  document.removeEventListener('keydown', trapFocusHandlers.keydown)
}

function trapFocus(container, elementToFocus = container) {
  const elementToRestore = document.activeElement

  removeTrapFocusListeners()
  previouslyFocusedElement = elementToRestore

  trapFocusHandlers.focusin = (event) => {
    const elements = getTabbableElements(container)
    const first = elements[0]
    const last = elements[elements.length - 1]

    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    ) {
      return
    }

    document.addEventListener('keydown', trapFocusHandlers.keydown)
  }

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown)
  }

  trapFocusHandlers.keydown = (event) => {
    const elements = getTabbableElements(container)
    const first = elements[0]
    const last = elements[elements.length - 1]

    if (event.code.toUpperCase() !== 'TAB' || !first || !last) return

    if (event.target === last && !event.shiftKey) {
      event.preventDefault()
      first.focus()
    }

    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault()
      last.focus()
    }
  }

  document.addEventListener('focusout', trapFocusHandlers.focusout)
  document.addEventListener('focusin', trapFocusHandlers.focusin)

  const focusTarget =
    (elementToFocus &&
      isProgrammaticallyFocusable(elementToFocus) &&
      elementToFocus) ||
    getTabbableElements(container)[0] ||
    container
  focusTarget.focus()

  if (
    focusTarget.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(focusTarget.type) &&
    focusTarget.value
  ) {
    focusTarget.setSelectionRange(0, focusTarget.value.length)
  }
}

function removeTrapFocus(elementToFocus = null) {
  removeTrapFocusListeners()

  const restoreTarget = elementToFocus || previouslyFocusedElement
  if (restoreTarget) restoreTarget.focus()
  previouslyFocusedElement = null
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return

  const openDetailsElement = event.target.closest('details[open]')
  if (!openDetailsElement) return

  const summaryElement = openDetailsElement.querySelector('summary')
  openDetailsElement.removeAttribute('open')
  if (summaryElement) {
    toggleAriaExpanded(summaryElement, false)
    summaryElement.focus()
  }
}

/**
 * Toggle or set aria-expanded on a dropdown/accordion trigger.
 * @param {Element} trigger
 * @param {boolean} [expanded] explicit state; omit to invert
 * @returns {boolean} the new expanded state
 */
function toggleAriaExpanded(trigger, expanded) {
  if (!trigger) return false

  const next =
    typeof expanded === 'boolean'
      ? expanded
      : trigger.getAttribute('aria-expanded') !== 'true'

  trigger.setAttribute('aria-expanded', String(next))
  return next
}

function bindAriaExpandedTriggers(root = document) {
  for (const trigger of root.querySelectorAll(ARIA_EXPANDED_TRIGGER_SELECTOR)) {
    if (trigger.dataset.a11yExpandedBound === 'true') continue

    trigger.dataset.a11yExpandedBound = 'true'
    if (!trigger.hasAttribute('aria-expanded')) {
      trigger.setAttribute('aria-expanded', 'false')
    }

    trigger.addEventListener('click', () => {
      toggleAriaExpanded(trigger)
    })
  }
}

function getNewWindowMessage() {
  return (
    document.getElementById('a11y-new-window-message')?.textContent?.trim() ||
    window.langMessages?.['accessibility.link_messages.new_window'] ||
    'Opens in a new window.'
  )
}

function hasNewWindowAnnouncement(link, message) {
  if (link.querySelector(`.${NEW_WINDOW_ANNOUNCEMENT_CLASS}`)) return true

  const describedBy = link.getAttribute('aria-describedby')
  if (describedBy?.split(/\s+/).includes('a11y-new-window-message')) return true

  const normalized = message.replace(/\.$/, '').toLowerCase()
  return Array.from(link.querySelectorAll('.visually-hidden')).some((el) =>
    el.textContent.toLowerCase().includes(normalized),
  )
}

function hardenNewWindowLink(link, message = getNewWindowMessage()) {
  if (!link || link.tagName !== 'A') return
  if (link.getAttribute('target') !== '_blank') return

  const relTokens = new Set(
    (link.getAttribute('rel') || '').split(/\s+/).filter(Boolean),
  )
  relTokens.add('noopener')
  relTokens.add('noreferrer')
  link.setAttribute('rel', [...relTokens].join(' '))

  if (hasNewWindowAnnouncement(link, message)) return

  const announcement = document.createElement('span')
  announcement.className = `visually-hidden visually-hidden--inline ${NEW_WINDOW_ANNOUNCEMENT_CLASS}`
  announcement.textContent = message
  link.appendChild(announcement)
}

function announceNewWindows(root = document) {
  const message = getNewWindowMessage()

  if (root.matches?.('a[target="_blank"]')) {
    hardenNewWindowLink(root, message)
  }

  for (const link of root.querySelectorAll('a[target="_blank"]')) {
    hardenNewWindowLink(link, message)
  }
}

function initNewWindowAnnouncer() {
  announceNewWindows()

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue
        announceNewWindows(node)
      }
    }
  })

  observer.observe(document.body, { childList: true, subtree: true })
}

function onReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback)
  } else {
    callback()
  }
}

onReady(() => {
  bindAriaExpandedTriggers()
  initNewWindowAnnouncer()
})
